import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CalendarDays, Users, FileText, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CRMDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ appointments: 0, customers: 0, todayAppointments: [], upcomingAppointments: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const today = new Date().toLocaleDateString("sv-SE");
      const [aptsRes, custRes] = await Promise.all([
        axios.get(`${API}/appointments`, { headers }),
        axios.get(`${API}/customers`, { headers }),
      ]);

      const allApts = aptsRes.data;
      const todayApts = allApts.filter(a => a.date === today && a.status !== "cancelled");
      const upcoming = allApts.filter(a => a.date >= today && a.status !== "cancelled").slice(0, 8);

      setStats({
        appointments: allApts.filter(a => a.status !== "cancelled").length,
        customers: custRes.data.length,
        todayAppointments: todayApts,
        upcomingAppointments: upcoming,
      });
    } catch { /* ignore */ }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short" });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-cyan-700 border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="crm-dashboard">
      <h1 className="font-['Manrope'] text-2xl font-bold text-slate-900 mb-6">Dashboard</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8">
        {[
          { icon: CalendarDays, label: "Termine gesamt", value: stats.appointments, color: "text-cyan-700", bg: "bg-cyan-50" },
          { icon: Clock, label: "Heute", value: stats.todayAppointments.length, color: "text-green-600", bg: "bg-green-50" },
          { icon: Users, label: "Kunden", value: stats.customers, color: "text-cyan-700", bg: "bg-cyan-50" },
          { icon: FileText, label: "Beratungsbögen", value: "-", color: "text-green-600", bg: "bg-green-50" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200/60 p-4 md:p-5" data-testid={`stat-card-${i}`}>
            <div className={`h-10 w-10 md:h-11 md:w-11 ${s.bg} rounded-xl flex items-center justify-center mb-3`}>
              <s.icon className={`h-5 w-5 ${s.color}`} />
            </div>
            <p className="text-xl md:text-2xl font-bold text-slate-900 font-['Manrope']">{s.value}</p>
            <p className="text-xs md:text-sm text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Upcoming appointments */}
      <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="font-['Manrope'] text-lg font-semibold text-slate-800">Kommende Termine</h2>
          <Button variant="ghost" onClick={() => navigate("/app/appointments")} className="text-sm text-cyan-700 hover:text-cyan-800">
            Alle anzeigen <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
        {stats.upcomingAppointments.length === 0 ? (
          <p className="text-sm text-slate-400 p-5">Keine anstehenden Termine</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {stats.upcomingAppointments.map((apt) => (
              <div key={apt.id} className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate(`/app/customers/${apt.customer_id}`)}>
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 bg-cyan-50 rounded-xl flex items-center justify-center shrink-0">
                    <CalendarDays className="h-5 w-5 text-cyan-700" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{apt.name}</p>
                    <p className="text-xs text-slate-500">{apt.email} | {apt.phone}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-700">{formatDate(apt.date)}</p>
                  <p className="text-xs text-slate-500">{apt.time_slot} Uhr</p>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                  apt.status === "confirmed" ? "bg-green-50 text-green-700" :
                  apt.status === "completed" ? "bg-slate-100 text-slate-600" :
                  apt.status === "cancelled" ? "bg-red-50 text-red-600" :
                  "bg-amber-50 text-amber-700"
                }`}>
                  {apt.status === "confirmed" ? "Bestätigt" : apt.status === "completed" ? "Abgeschlossen" : apt.status === "cancelled" ? "Storniert" : "Offen"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
